import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { EditLayer, Field, Notice, Switch } from '@/admin/AdminShell'
import type { CellPreview, CellTemplateInput, ZoneRow } from './topologyApi'
import { generateCells, previewCells } from './topologyApi'

/**
 * Генератор ячеек по шаблону: диапазоны рядов, стеллажей, ярусов и позиций плюс маска кода.
 *
 * **Сначала предпросмотр, потом создание.** Кнопка «Создать N ячеек» появляется только
 * после предпросмотра и только для того шаблона, который в нём показан: поменяли диапазон —
 * предпросмотр устарел, и кнопка снова просит его обновить. Иначе число на кнопке
 * описывало бы не то, что уйдёт на сервер.
 *
 * Уже существующие коды пропускаются, а не дублируются и не валят пачку: стеллаж,
 * наращённый ярусом, описывается тем же шаблоном с длинным диапазоном. Предпросмотр
 * называет, сколько пропустится, и показывает эти коды.
 *
 * Разбор шаблона и потолок (5000 ячеек за раз) — на сервере: экран показывает его отказ
 * словами, а не повторяет правила своей копией, которая разошлась бы с ним.
 */
export function CellGenerator({ zone, maxCells, defaultMask, onClose, onCreated }: {
  zone: ZoneRow
  maxCells: number
  defaultMask: string
  onClose: () => void
  onCreated: () => void
}) {
  const { t } = useApp()
  const [draft, setDraft] = useState({
    rowFrom: 'A', rowTo: 'A', rackFrom: '1', rackTo: '10', tierFrom: '1', tierTo: '4', positionFrom: '1', positionTo: '2',
    mask: defaultMask, maxWeightKg: '', maxVolumeM3: '', maxPallets: '', lengthMm: '', widthMm: '', heightMm: '',
    abcClass: '', pickable: true,
  })
  const [preview, setPreview] = useState<{ body: string; result: CellPreview } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{ created: number; skipped: number } | null>(null)

  const numbers = [draft.maxWeightKg, draft.maxVolumeM3, draft.maxPallets, draft.lengthMm, draft.widthMm, draft.heightMm]
    .map((value) => (value.trim() === '' ? null : Number(value.replace(',', '.'))))
  const limitsInvalid = numbers.some((value) => value !== null && (Number.isNaN(value) || value <= 0))

  const body: CellTemplateInput = {
    rowFrom: draft.rowFrom.trim(), rowTo: draft.rowTo.trim(),
    rackFrom: draft.rackFrom.trim(), rackTo: draft.rackTo.trim(),
    tierFrom: draft.tierFrom.trim(), tierTo: draft.tierTo.trim(),
    positionFrom: draft.positionFrom.trim(), positionTo: draft.positionTo.trim(),
    mask: draft.mask.trim(),
    maxWeightKg: numbers[0] ?? null, maxVolumeM3: numbers[1] ?? null, maxPallets: numbers[2] ?? null,
    lengthMm: numbers[3] ?? null, widthMm: numbers[4] ?? null, heightMm: numbers[5] ?? null,
    abcClass: draft.abcClass || null, pickable: draft.pickable,
  }
  const signature = JSON.stringify(body)
  const current = preview != null && preview.body === signature ? preview.result : null

  const runPreview = useMutation({
    mutationFn: () => previewCells(zone.id, body),
    onSuccess: (result) => { setError(null); setPreview({ body: signature, result }) },
    onError: (failure) => { setPreview(null); setError(failure instanceof Error ? failure.message : t('app.error')) },
  })
  const create = useMutation({
    mutationFn: () => generateCells(zone.id, body),
    onSuccess: (result) => { setDone({ created: result.created, skipped: result.skipped }); onCreated() },
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  const set = (patch: Partial<typeof draft>) => { setDone(null); setDraft({ ...draft, ...patch }) }

  const rangeField = (axis: 'row' | 'rack' | 'tier' | 'position') => {
    const from = `${axis}From` as const
    const to = `${axis}To` as const
    return (
      <Field label={t(`topo.gen.${axis}`)} htmlFor={`gen-${axis}-from`}>
        <span className="row" style={{ gap: 6 }}>
          <input id={`gen-${axis}-from`} className="input mono" aria-label={t('topo.gen.from', { axis: t(`topo.gen.${axis}`) })}
                 value={draft[from]} onChange={(e) => set({ [from]: e.target.value })} style={{ width: 72 }} />
          <span aria-hidden="true">…</span>
          <input className="input mono" aria-label={t('topo.gen.to', { axis: t(`topo.gen.${axis}`) })}
                 value={draft[to]} onChange={(e) => set({ [to]: e.target.value })} style={{ width: 72 }} />
        </span>
      </Field>
    )
  }

  const limit = (key: 'maxWeightKg' | 'maxVolumeM3' | 'maxPallets' | 'lengthMm' | 'widthMm' | 'heightMm', label: string) => (
    <Field label={label} htmlFor={`gen-${key}`}>
      <input id={`gen-${key}`} className="input mono" inputMode="decimal" value={draft[key]} onChange={(e) => set({ [key]: e.target.value })} />
    </Field>
  )

  const footer = done ? (
    <button type="button" className="btn btn-primary" onClick={onClose}>{t('adm.close')}</button>
  ) : (
    <>
      <button type="button" className="btn btn-secondary" onClick={onClose}>{t('admin.cancel')}</button>
      <button type="button" className="btn btn-secondary" disabled={runPreview.isPending || limitsInvalid}
              onClick={() => runPreview.mutate()}>
        {runPreview.isPending ? t('topo.gen.previewing') : t('topo.gen.preview')}
      </button>
      <button type="button" className="btn btn-primary" disabled={!current || current.toCreate === 0 || create.isPending}
              onClick={() => create.mutate()}>
        {current
          ? t('topo.gen.create', { count: current.toCreate })
          : t('topo.gen.createNeedsPreview')}
      </button>
    </>
  )

  return (
    <EditLayer kind="modal" overline={t('topo.gen.overline', { zone: zone.code })} title={t('topo.gen.title')}
               subtitle={t('topo.gen.subtitle', { max: maxCells })} onClose={onClose} footer={footer}>
      <div className="stack" style={{ gap: 14 }}>
        <div className="form-grid">
          {rangeField('row')}
          {rangeField('rack')}
        </div>
        <div className="form-grid">
          {rangeField('tier')}
          {rangeField('position')}
        </div>
        <Field label={t('topo.gen.mask')} htmlFor="gen-mask" help={t('topo.gen.mask.help')}>
          <input id="gen-mask" className="input mono" value={draft.mask} onChange={(e) => set({ mask: e.target.value })} />
        </Field>
        <details>
          <summary className="t-sm">{t('topo.gen.defaults')}</summary>
          <div className="stack" style={{ gap: 10, marginTop: 10 }}>
            <div className="form-grid">
              {limit('maxWeightKg', t('topo.cells.maxWeight'))}
              {limit('maxVolumeM3', t('topo.cells.maxVolume'))}
            </div>
            <div className="form-grid">
              {limit('maxPallets', t('topo.cells.maxPallets'))}
              <Field label={t('topo.cells.abc')} htmlFor="gen-abc">
                <select id="gen-abc" className="input" value={draft.abcClass} onChange={(e) => set({ abcClass: e.target.value })}>
                  <option value="">—</option>
                  {['A', 'B', 'C'].map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              </Field>
            </div>
            <div className="form-grid">
              {limit('lengthMm', t('topo.cells.length'))}
              {limit('widthMm', t('topo.cells.width'))}
            </div>
            <div className="form-grid">
              {limit('heightMm', t('topo.cells.height'))}
              <Field label={t('topo.cells.pickable')}>
                <Switch checked={draft.pickable} ariaLabel={t('topo.cells.pickable')} onChange={(value) => set({ pickable: value })} />
              </Field>
            </div>
            {limitsInvalid && <Notice text={t('topo.cells.limitInvalid')} />}
          </div>
        </details>

        <Notice text={error} />
        {done ? (
          <div className="alert alert-neutral" role="status">
            <span>{t('topo.gen.done', { created: done.created, skipped: done.skipped })}</span>
          </div>
        ) : current ? (
          <section className="stack" aria-label={t('topo.gen.previewTitle')} style={{ gap: 6 }}>
            <span className="strong">
              {t('topo.gen.summary', { total: current.total, create: current.toCreate, existing: current.existing })}
            </span>
            {current.toCreate > 0 && (
              <span className="t-sm">
                {t('topo.gen.codes')}{' '}
                <span className="mono">{current.first.join(', ')}{current.last.length > 0 ? ` … ${current.last.join(', ')}` : ''}</span>
              </span>
            )}
            {current.existing > 0 && (
              <span className="t-sm muted">
                {t('topo.gen.skipped')} <span className="mono">{current.existingCodes.join(', ')}{current.existing > current.existingCodes.length ? ' …' : ''}</span>
              </span>
            )}
            {current.toCreate === 0 && <span className="t-sm muted">{t('topo.gen.nothingNew')}</span>}
          </section>
        ) : preview ? (
          <p className="t-sm muted" role="status">{t('topo.gen.stale')}</p>
        ) : null}
      </div>
    </EditLayer>
  )
}
